import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = '';

// Generate or get session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('visitor_session');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    sessionStorage.setItem('visitor_session', sessionId);
  }
  return sessionId;
};

// Parse UTM parameters from URL
const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_term: params.get('utm_term'),
    utm_content: params.get('utm_content'),
    gclid: params.get('gclid'), // Google Ads Click ID
    fbclid: params.get('fbclid'), // Facebook Click ID
    msclkid: params.get('msclkid') // Microsoft/Bing Ads Click ID
  };
};

// Determine traffic source from referrer
const getTrafficSource = (referrer) => {
  if (!referrer) return { source: 'direct', medium: 'none' };
  
  const url = referrer.toLowerCase();
  
  // Search engines
  if (url.includes('google.')) return { source: 'google', medium: 'organic' };
  if (url.includes('bing.')) return { source: 'bing', medium: 'organic' };
  if (url.includes('yahoo.')) return { source: 'yahoo', medium: 'organic' };
  if (url.includes('duckduckgo.')) return { source: 'duckduckgo', medium: 'organic' };
  
  // Social media
  if (url.includes('facebook.') || url.includes('fb.')) return { source: 'facebook', medium: 'social' };
  if (url.includes('instagram.')) return { source: 'instagram', medium: 'social' };
  if (url.includes('twitter.') || url.includes('t.co')) return { source: 'twitter', medium: 'social' };
  if (url.includes('linkedin.')) return { source: 'linkedin', medium: 'social' };
  if (url.includes('tiktok.')) return { source: 'tiktok', medium: 'social' };
  if (url.includes('youtube.')) return { source: 'youtube', medium: 'social' };
  
  // Extract domain for other referrers
  try {
    const domain = new URL(referrer).hostname.replace('www.', '');
    return { source: domain, medium: 'referral' };
  } catch {
    return { source: 'unknown', medium: 'referral' };
  }
};

// Store first touch attribution
const getFirstTouchAttribution = () => {
  let attribution = sessionStorage.getItem('traffic_attribution');
  if (!attribution) {
    const utmParams = getUTMParams();
    const trafficSource = getTrafficSource(document.referrer);
    
    // Detect Google Ads (gclid parameter)
    if (utmParams.gclid) {
      attribution = {
        source: 'google',
        medium: 'cpc',
        campaign: utmParams.utm_campaign || 'google_ads',
        keyword: utmParams.utm_term || null,
        gclid: utmParams.gclid,
        referrer: document.referrer || null,
        landing_page: window.location.pathname,
        timestamp: new Date().toISOString()
      };
    }
    // Detect Microsoft/Bing Ads (msclkid parameter)
    else if (utmParams.msclkid) {
      attribution = {
        source: 'bing',
        medium: 'cpc',
        campaign: utmParams.utm_campaign || 'bing_ads',
        keyword: utmParams.utm_term || null,
        msclkid: utmParams.msclkid,
        referrer: document.referrer || null,
        landing_page: window.location.pathname,
        timestamp: new Date().toISOString()
      };
    }
    // Detect Facebook Ads (fbclid parameter)
    else if (utmParams.fbclid) {
      attribution = {
        source: 'facebook',
        medium: 'cpc',
        campaign: utmParams.utm_campaign || 'facebook_ads',
        keyword: utmParams.utm_term || null,
        fbclid: utmParams.fbclid,
        referrer: document.referrer || null,
        landing_page: window.location.pathname,
        timestamp: new Date().toISOString()
      };
    }
    // UTM params override referrer detection
    else {
      attribution = {
        source: utmParams.utm_source || trafficSource.source,
        medium: utmParams.utm_medium || trafficSource.medium,
        campaign: utmParams.utm_campaign || null,
        keyword: utmParams.utm_term || null,
        referrer: document.referrer || null,
        landing_page: window.location.pathname,
        timestamp: new Date().toISOString()
      };
    }
    
    sessionStorage.setItem('traffic_attribution', JSON.stringify(attribution));
  } else {
    attribution = JSON.parse(attribution);
  }
  return attribution;
};

const Analytics = () => {
  const location = useLocation();

  useEffect(() => {
    const trackPageView = async () => {
      try {
        const attribution = getFirstTouchAttribution();
        
        // Track page view with traffic source
        await axios.post(`${API}/api/analytics/pageview`, {
          page: location.pathname,
          referrer: document.referrer || null,
          user_agent: navigator.userAgent,
          traffic_source: attribution.source,
          traffic_medium: attribution.medium,
          campaign: attribution.campaign,
          keyword: attribution.keyword
        });

        // Track visitor
        await axios.post(`${API}/api/analytics/visitor`, {
          session_id: getSessionId(),
          page: location.pathname,
          referrer: document.referrer || null,
          user_agent: navigator.userAgent,
          screen_width: window.innerWidth,
          screen_height: window.innerHeight,
          traffic_source: attribution.source,
          traffic_medium: attribution.medium,
          campaign: attribution.campaign,
          keyword: attribution.keyword
        });
      } catch (error) {
        // Silent fail - don't break the app if analytics fails
        console.debug('Analytics tracking error:', error);
      }
    };

    trackPageView();

    // Keep visitor session alive
    const heartbeat = setInterval(async () => {
      try {
        const attribution = getFirstTouchAttribution();
        await axios.post(`${API}/api/analytics/visitor`, {
          session_id: getSessionId(),
          page: location.pathname,
          user_agent: navigator.userAgent,
          screen_width: window.innerWidth,
          screen_height: window.innerHeight,
          traffic_source: attribution.source,
          traffic_medium: attribution.medium
        });
      } catch (error) {
        // Silent fail
      }
    }, 60000); // Every minute

    return () => clearInterval(heartbeat);
  }, [location.pathname]);

  return null; // This component doesn't render anything
};

// Export function to get traffic attribution for forms
export const getTrafficAttribution = () => {
  return getFirstTouchAttribution();
};

export default Analytics;
